import { v4 as uuid } from 'uuid'
import { Filter } from 'components/filters/useStreamFilters'
import styles from 'components/panels/panel-container.module.scss'
import { isMobile } from 'lib/device'
import { findAnswerTargetElement, scrollAnswerElementIntoView } from 'lib/answerTargetScroll'
import { getAnswerHighlightTargets } from 'lib/answerHighlightTargets'
import { getAnswerHighlightHoldMs } from 'lib/answerHighlightDuration'
import {
    AnswerRevealCoordinator,
    type AnswerRevealIntent,
    type AnswerRevealResult,
} from 'lib/answerRevealCoordinator'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'

export type PanelId = string
export type QuestId = string
export type AnswerScrollResult = AnswerRevealResult
export type AnswerScrollOptions = {
    deadlineAt?: number
    instant?: boolean
    contextScroll?: boolean
    shouldCancel?: () => boolean
}
export type AnswerScroller = (
    answerId: string,
    options?: AnswerScrollOptions,
) => AnswerScrollResult | Promise<AnswerScrollResult>
export type CanRevealAnswer = (answerId: string) => boolean
export type RevealAnswerOptions = {
    questId?: string
    deadlineAt?: number
    instant?: boolean
    contextScroll?: boolean
    highlight?: boolean
    shake?: boolean
    shakeQuestByAnswer?: boolean
    shakeQuestId?: string
    onRevealed?: (result: AnswerRevealResult) => void
}
// One-shot navigation intent: the next thread content commit for this quest+answer
// places the scroll position pre-paint instead of running the animated reveal.
export type OpenPlacementIntent = {
    questId: string
    answerId: string
    contextScroll?: boolean
}

export enum DefaultStreamFilters {
    public = 'public',
    private = 'private',
    notifications = 'notifications',
    link = 'link',
    all = 'all',
}

export const ANSWER_HEIGHT = isMobile ? 128 : 121
export const SCROLL_OFFSET = 1500

export class PanelState {
    panelId: PanelId = uuid()
    empty: boolean = false
    newThread: boolean = false
    draftFilter?: Filter
    stream?: `${DefaultStreamFilters}` | { teamId: string } = 'public'
    filter?: Filter
    questId?: QuestId
    query?: string
    // Persist scroll position across remounts and mode switches
    savedScrollTop: number = 0
    // Ephemeral: paint panel chrome first, then mount heavy content after Swiper settles.
    deferContentUntilTransition: boolean = false
    // Ephemeral navigation intent (never session-persisted): consumed by the first thread
    // content commit that can place the target answer pre-paint.
    pendingOpenPlacement?: OpenPlacementIntent
    // `${questId}:${answerId}` once a pre-paint placement applied. The follow-up reveal
    // verification must be instant — never the staged rewind animation.
    openPlacementAppliedKey?: string

    private readonly MAX_SCROLL_ATTEMPTS = 5
    private readonly SCROLL_TIMEOUT = 3000

    private refreshHandler?: () => void | Promise<void>
    private answerScroller?: AnswerScroller
    private answerScrollerQuestId?: string
    private answerScrollerCanReveal?: CanRevealAnswer
    private answerScrollGeneration = 0
    private readonly answerRevealReadinessBlockers: Set<string>
    private readonly answerRevealCoordinator: AnswerRevealCoordinator
    private answerRevealCancellationCleanup?: () => void

    // Ephemeral flag to request a bottom scroll when the soft keyboard opens
    private _pendingScrollToBottom: boolean = false
    // Ephemeral flag to request revealing the NewAnswer editor when keyboard opens
    private _pendingRevealNewAnswer: boolean = false

    constructor(panel?: Partial<PanelState>) {
        if (panel) {
            Object.assign(this, panel)
        }
        this.answerRevealReadinessBlockers = new Set<string>()
        this.answerRevealCoordinator = new AnswerRevealCoordinator({
            canReveal: intent => this.canStartAnswerReveal(intent),
            reveal: (intent, shouldCancel) => this.performAnswerReveal(intent, shouldCancel),
            onStart: () => this.beginAnswerReveal(),
            onReveal: () => this.beginClaimedAnswerReveal(),
            onComplete: (intent, result) => this.completeAnswerReveal(intent, result),
        })
        if (this.deferContentUntilTransition) {
            this.answerRevealReadinessBlockers.add('deferred-panel-content')
        }
    }

    private panelRef?: HTMLDivElement
    setPanelRef = (ref: HTMLDivElement) => {
        this.panelRef = ref
    }
    getPanelRef = () => this.panelRef

    // How far, in feed viewports, an animated same-panel jump may travel. Longer jumps
    // teleport to within this distance first so the glide only crosses content adjacent
    // to the destination — animating the full virtualized range is what made long
    // smooth scrolls slow and unreliable.
    private readonly GLIDE_MAX_VIEWPORTS = 1.25

    private prefersReducedMotion = () =>
        typeof window !== 'undefined' &&
        Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)

    // Only animate a jump the user can watch: the container must be laid out, painted,
    // and on screen (inactive mobile slides are translated off-viewport, not unmounted).
    private canAnimateScroll = (scrollContainer: HTMLElement) => {
        if (this.prefersReducedMotion()) return false
        const rect = scrollContainer.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) return false
        if (rect.bottom <= 0 || rect.right <= 0) return false
        if (rect.top >= window.innerHeight || rect.left >= window.innerWidth) return false
        const { checkVisibility } = scrollContainer as HTMLElement & {
            checkVisibility?: (options?: {
                checkOpacity?: boolean
                checkVisibilityCSS?: boolean
                opacityProperty?: boolean
                visibilityProperty?: boolean
            }) => boolean
        }
        if (checkVisibility) {
            try {
                return checkVisibility.call(scrollContainer, {
                    // The first pair covers older implementations of the draft API;
                    // the second pair is the current standard spelling.
                    checkOpacity: true,
                    checkVisibilityCSS: true,
                    opacityProperty: true,
                    visibilityProperty: true,
                })
            } catch {
                // Fall through for WebViews whose partial implementation rejects options.
            }
        }

        // Older WebViews do not expose checkVisibility(). Walk the rendered ancestor
        // styles so visibility-hidden prewarms and opacity-hidden side panels stay instant.
        let element: HTMLElement | null = scrollContainer
        while (element) {
            const style = window.getComputedStyle(element)
            const opacity = Number.parseFloat(style.opacity)
            if (
                style.display === 'none' ||
                style.visibility === 'hidden' ||
                style.visibility === 'collapse' ||
                (Number.isFinite(opacity) && opacity <= 0.01)
            ) {
                return false
            }
            element = element.parentElement
        }
        return true
    }

    private glideDistance = (scrollContainer: HTMLElement) =>
        Math.round(scrollContainer.clientHeight * this.GLIDE_MAX_VIEWPORTS)

    // Bring the feed back to the top. Panels the user cannot see jump instantly; a
    // watched panel teleports to within one glide-length of the top and smooth-scrolls
    // the visible remainder (with retry, since virtualized content can shift mid-flight).
    private resetScrollToTop = (scrollContainer: HTMLElement) => {
        const currentTop = scrollContainer.scrollTop || 0
        if (currentTop <= 2) return
        if (!this.canAnimateScroll(scrollContainer)) {
            scrollContainer.scrollTop = 0
            return
        }
        const glide = this.glideDistance(scrollContainer)
        if (currentTop > glide) {
            scrollContainer.scrollTop = glide
        }
        this.scrollWithRetry(
            scrollContainer,
            () => 0,
            () => (scrollContainer.scrollTop || 0) <= 2,
        )
    }

    triggerStreamRefresh = (options?: { force?: boolean; smoothScroll?: boolean }) => {
        const refreshHandler = this.refreshHandler
        if (!refreshHandler) return false

        let triggered = false
        const executeTrigger = () => {
            if (triggered) return true
            triggered = true
            refreshHandler()
            return true
        }

        const scrollContainer = this.getScrollContainer()

        if (options?.smoothScroll === false) {
            this.savedScrollTop = 0
            if (scrollContainer) {
                // The refresh below stays synchronous — the reset glide is cosmetic and
                // an interrupted animation must never swallow the triggered refresh.
                this.resetScrollToTop(scrollContainer)
            }
            return executeTrigger()
        }

        if (!scrollContainer) {
            return executeTrigger()
        }

        if ((scrollContainer.scrollTop || 0) <= 2) {
            return executeTrigger()
        }
        this.scrollWithRetry(
            scrollContainer,
            () => 0,
            () => (scrollContainer.scrollTop || 0) <= 2,
            () => {
                executeTrigger()
            },
        )

        return true
    }

    registerRefreshHandler = (handler: (() => void | Promise<void>) | null) => {
        this.refreshHandler = handler || undefined
    }

    registerAnswerScroller = (
        handler: AnswerScroller | null,
        questId?: string | number,
        canRevealAnswer?: CanRevealAnswer,
    ) => {
        this.answerScroller = handler || undefined
        this.answerScrollerQuestId = handler && questId != null ? String(questId) : undefined
        this.answerScrollerCanReveal = handler ? canRevealAnswer : undefined
        if (handler) this.answerRevealCoordinator.notifyReady()

        return () => {
            if (this.answerScroller === handler) {
                this.answerScroller = undefined
                this.answerScrollerQuestId = undefined
                this.answerScrollerCanReveal = undefined
            }
        }
    }

    cancelPendingAnswerScroll = () => {
        this.answerScrollGeneration += 1
        this.answerRevealCoordinator.cancel()
    }

    // Single-shot: true exactly once per matching intent, so re-renders, hydration, and
    // session revisions can never place twice.
    consumeOpenPlacement = (questId: string, answerId: string): boolean => {
        const intent = this.pendingOpenPlacement
        if (!intent || !questId || !answerId) return false
        if (
            String(intent.questId) !== String(questId) ||
            String(intent.answerId) !== String(answerId)
        ) {
            return false
        }
        this.pendingOpenPlacement = undefined
        return true
    }

    markOpenPlacementApplied = (questId: string, answerId: string) => {
        this.openPlacementAppliedKey = `${String(questId)}:${String(answerId)}`
    }

    hasAppliedOpenPlacement = (questId: string, answerId: string) =>
        !!this.openPlacementAppliedKey &&
        this.openPlacementAppliedKey === `${String(questId)}:${String(answerId)}`

    blockAnswerReveal = (source: string) => {
        if (!source) return () => {}
        this.answerRevealReadinessBlockers.add(source)

        let released = false
        return () => {
            if (released) return
            released = true
            this.answerRevealReadinessBlockers.delete(source)
            this.answerRevealCoordinator.notifyReady()
        }
    }

    setAnswerRevealReady = (source: string, ready: boolean) => {
        if (!source) return
        if (ready) {
            this.answerRevealReadinessBlockers.delete(source)
            this.answerRevealCoordinator.notifyReady()
        } else {
            this.answerRevealReadinessBlockers.add(source)
        }
    }

    shake = () => {
        const panelRef = this.getPanelRef()
        if (panelRef) {
            panelRef.classList.add(styles.shake)
            setTimeout(() => {
                panelRef.classList.remove(styles.shake)
            }, 400) // Duration of the animation
        }
    }

    private shakeElement = (el?: HTMLElement | null) => {
        if (!el) return
        // `translate`, not `transform`, so an element's own transform (a
        // side window's scale) holds through the shake.
        el.animate(
            [
                { translate: '0' },
                { translate: '-5px 0' },
                { translate: '5px 0' },
                { translate: '-5px 0' },
                { translate: '0' },
            ],
            { duration: 400, easing: 'ease-in-out' },
        )
    }

    shakeQuest = (questId: string, retryCount = 0) => {
        if (!questId) return
        const questElement = this.panelRef?.querySelector(`#quest-${questId}`) as HTMLElement | null
        if (!questElement) {
            if (retryCount < 3) {
                requestAnimationFrame(() => this.shakeQuest(questId, retryCount + 1))
            }
            return
        }
        this.shakeElement(questElement)
    }

    shakeQuestByAnswer = (answerId: string, retryCount = 0) => {
        if (!answerId) return
        const answerEl = findAnswerTargetElement(this.panelRef, answerId)
        const questElement = answerEl?.closest('[id^="quest-"]') as HTMLElement | null
        if (!questElement) {
            if (retryCount < 3) {
                requestAnimationFrame(() => this.shakeQuestByAnswer(answerId, retryCount + 1))
            }
            return
        }
        this.shakeElement(questElement)
    }

    shakeAndHighlightAnswer = (answerId: string, callback?: () => void) => {
        void this.requestAnswerReveal(answerId, {
            shake: true,
            highlight: true,
            onRevealed: result => {
                if (result === 'done') callback?.()
            },
        })
    }

    getTransformContainer = () => this.panelRef?.querySelector('.transform-container')
    getScrollContainer = () => this.panelRef?.querySelector('.scroll-container') as HTMLElement

    // Public API for components to request a bottom scroll on next keyboard open
    requestScrollToBottomOnKeyboardOpen = () => {
        this._pendingScrollToBottom = true
    }

    hasPendingScrollToBottom = () => this._pendingScrollToBottom === true

    consumePendingScrollToBottom = () => {
        const was = this._pendingScrollToBottom
        this._pendingScrollToBottom = false
        return was
    }

    // Public API for components to request reveal of the NewAnswer on next keyboard open
    requestRevealNewAnswerOnKeyboardOpen = () => {
        this._pendingRevealNewAnswer = true
    }

    hasPendingRevealNewAnswer = () => this._pendingRevealNewAnswer === true

    consumePendingRevealNewAnswer = () => {
        const was = this._pendingRevealNewAnswer
        this._pendingRevealNewAnswer = false
        return was
    }

    // Minimally scroll so that targetEl's bottom is within bottomMargin of the visible bottom
    // and its top is at least topMargin from the visible top. If smooth === false, use instant.
    scrollToRevealElement = (
        targetEl: HTMLElement,
        options?: { bottomMargin?: number; topMargin?: number; smooth?: boolean },
    ) => {
        const scrollContainer = this.getScrollContainer()
        if (!scrollContainer || !targetEl) return

        const bottomMargin = options?.bottomMargin ?? 30
        const topMargin = options?.topMargin ?? 30
        const smooth = options?.smooth ?? true

        const containerRect = scrollContainer.getBoundingClientRect()
        const targetRect = targetEl.getBoundingClientRect()
        const desiredBottom = containerRect.top + scrollContainer.clientHeight - bottomMargin
        const desiredTop = containerRect.top + topMargin

        const currentTop = scrollContainer.scrollTop || 0
        let targetScrollTop = currentTop

        if (targetRect.bottom > desiredBottom) {
            const delta = targetRect.bottom - desiredBottom
            targetScrollTop = currentTop + delta
        } else if (targetRect.top < desiredTop) {
            const delta = desiredTop - targetRect.top
            targetScrollTop = Math.max(0, currentTop - delta)
        } else {
            // Already within margins
            return
        }

        try {
            scrollContainer.scrollTo({
                top: targetScrollTop,
                behavior: smooth ? 'smooth' : 'auto',
            })
        } catch (e) {
            scrollContainer.scrollTop = targetScrollTop
        }
    }

    private scrollWithRetry = (
        scrollContainer: HTMLElement,
        getTargetScroll: () => number,
        checkIfReachedTarget: () => boolean,
        callback?: () => void,
    ) => {
        let isUserScrolling = false
        let scrollAttempts = 0

        const attemptScroll = () => {
            let lastScrollTop = scrollContainer.scrollTop
            let lastCheckTime = Date.now()
            let scrollTimeout: number
            let isScrolling = false
            let hasCompleted = false
            let maxTimeoutId: number
            let callbackCalled = false

            const callbackOnce = () => {
                if (!callbackCalled && callback) {
                    callbackCalled = true
                    callback()
                }
            }

            const cleanup = () => {
                if (!hasCompleted) {
                    hasCompleted = true
                    scrollContainer.removeEventListener('scroll', scrollHandler)
                    window.clearTimeout(scrollTimeout)
                    window.clearTimeout(maxTimeoutId)
                }
            }

            if (isUserScrolling || scrollAttempts >= this.MAX_SCROLL_ATTEMPTS) {
                cleanup()
                return
            }

            scrollAttempts++

            const checkScrollEnd = () => {
                const now = Date.now()
                const scrollTop = scrollContainer.scrollTop

                // Only consider scroll ended if we've been at the same position for at least 100ms
                if (scrollTop === lastScrollTop && now - lastCheckTime >= 100) {
                    if (!checkIfReachedTarget()) {
                        // Try scrolling again if we haven't reached the target
                        isScrolling = true
                        scrollContainer.scrollTo({
                            top: getTargetScroll(),
                            behavior: 'smooth',
                        })
                        lastScrollTop = scrollContainer.scrollTop
                        lastCheckTime = now
                        scrollTimeout = window.setTimeout(checkScrollEnd, 100)
                    } else {
                        callbackOnce()
                        cleanup()
                    }
                } else if (scrollTop !== lastScrollTop) {
                    // Still scrolling, update last position and time
                    lastScrollTop = scrollTop
                    lastCheckTime = now
                    scrollTimeout = window.setTimeout(checkScrollEnd, 100)
                } else {
                    // Same position but not long enough, check again
                    scrollTimeout = window.setTimeout(checkScrollEnd, 50)
                }
            }

            const scrollHandler = () => {
                if (!isScrolling) {
                    isUserScrolling = true
                    cleanup()
                } else {
                    // Reset the check timer since we're still scrolling
                    lastCheckTime = Date.now()
                    window.clearTimeout(scrollTimeout)
                    scrollTimeout = window.setTimeout(checkScrollEnd, 100)
                }
            }

            scrollContainer.addEventListener('scroll', scrollHandler)

            // Set a timeout to prevent infinite scrolling
            maxTimeoutId = window.setTimeout(() => {
                cleanup()
            }, this.SCROLL_TIMEOUT)

            // Initial scroll attempt
            isScrolling = true
            lastCheckTime = Date.now()
            scrollContainer.scrollTo({
                top: getTargetScroll(),
                behavior: 'smooth',
            })
            scrollTimeout = window.setTimeout(checkScrollEnd, 100)
        }

        attemptScroll()
    }

    scrollToAnswer = (
        answerId: string,
        callback?: () => void,
        options?: Pick<AnswerScrollOptions, 'instant'>,
    ) => {
        if (!answerId) return

        const scrollContainer = this.getScrollContainer()
        if (!scrollContainer) return

        const answerElement = answerId ? findAnswerTargetElement(scrollContainer, answerId) : null

        if (!answerElement) return

        const scrollGeneration = ++this.answerScrollGeneration
        const shouldCancel = () => scrollGeneration !== this.answerScrollGeneration

        requestAnimationFrame(() => {
            if (shouldCancel()) return

            const currentAnswerElement = findAnswerTargetElement(scrollContainer, answerId)
            if (!currentAnswerElement) return

            void scrollAnswerElementIntoView(scrollContainer, currentAnswerElement, {
                durationMs: options?.instant ? 0 : undefined,
                leadOffsetPx: options?.instant ? 0 : undefined,
                shouldCancel,
            }).then(reached => {
                if (reached) callback?.()
            })
        })
    }

    private scrollToAnswerDomResult = async (
        answerId: string,
        options: AnswerScrollOptions = {},
    ): Promise<AnswerScrollResult> => {
        if (!answerId) return 'not-in-quest'
        const scrollContainer = this.getScrollContainer()
        const answerElement = answerId ? findAnswerTargetElement(scrollContainer, answerId) : null

        if (!scrollContainer || !answerElement) return 'not-mounted'
        const revealed = await scrollAnswerElementIntoView(scrollContainer, answerElement, {
            durationMs: options.instant ? 0 : undefined,
            leadOffsetPx: options.instant ? 0 : undefined,
            shouldCancel: options.shouldCancel,
        })
        return revealed ? 'done' : options.shouldCancel?.() ? 'cancelled' : 'timeout'
    }

    private canStartAnswerReveal = (intent: AnswerRevealIntent) => {
        // After a pre-paint placement the reveal is verification only (anchored early-exit,
        // no motion), so it need not wait out the open-transition readiness blockers.
        const placementOwnsIntent =
            !!intent.questId && this.hasAppliedOpenPlacement(intent.questId, intent.answerId)
        if (this.answerRevealReadinessBlockers.size > 0 && !placementOwnsIntent) return false

        const scrollContainer = this.getScrollContainer()
        if (!scrollContainer || scrollContainer.clientHeight <= 0) return false

        const matchingScroller =
            this.answerScroller &&
            (!intent.questId ||
                !this.answerScrollerQuestId ||
                intent.questId === this.answerScrollerQuestId) &&
            (!this.answerScrollerCanReveal || this.answerScrollerCanReveal(intent.answerId))
        const requiresRegisteredScroller =
            Boolean(intent.questId) && String(this.filter?.questId || '') === intent.questId
        if (requiresRegisteredScroller) return Boolean(matchingScroller)

        return Boolean(
            matchingScroller || findAnswerTargetElement(scrollContainer, intent.answerId),
        )
    }

    private attachAnswerRevealCancellation = () => {
        const scrollContainer = this.getScrollContainer()
        const userScrollKeys = new Set([
            'ArrowDown',
            'ArrowUp',
            'End',
            'Home',
            'PageDown',
            'PageUp',
            ' ',
        ])
        const cancel = () => this.cancelPendingAnswerScroll()
        const onKeyDown = (event: KeyboardEvent) => {
            if (userScrollKeys.has(event.key)) cancel()
        }

        scrollContainer?.addEventListener('wheel', cancel, { passive: true })
        scrollContainer?.addEventListener('touchstart', cancel, { passive: true })
        scrollContainer?.addEventListener('pointerdown', cancel, { passive: true })
        window.addEventListener('keydown', onKeyDown)

        return () => {
            scrollContainer?.removeEventListener('wheel', cancel)
            scrollContainer?.removeEventListener('touchstart', cancel)
            scrollContainer?.removeEventListener('pointerdown', cancel)
            window.removeEventListener('keydown', onKeyDown)
        }
    }

    private performAnswerReveal = async (
        intent: AnswerRevealIntent,
        shouldCancel: () => boolean,
    ): Promise<AnswerScrollResult> => {
        const registeredScroller =
            this.answerScroller &&
            (!intent.questId ||
                !this.answerScrollerQuestId ||
                intent.questId === this.answerScrollerQuestId) &&
            (!this.answerScrollerCanReveal || this.answerScrollerCanReveal(intent.answerId))
                ? this.answerScroller
                : undefined
        if (registeredScroller) {
            const result = await registeredScroller(intent.answerId, {
                deadlineAt: intent.deadlineAt,
                instant: intent.instant,
                contextScroll: intent.contextScroll,
                shouldCancel,
            })
            if (result !== 'fallback' && result !== 'not-in-quest') return result
        }

        if (shouldCancel()) return 'cancelled'
        return this.scrollToAnswerDomResult(intent.answerId, {
            deadlineAt: intent.deadlineAt,
            instant: intent.instant,
            shouldCancel,
        })
    }

    private beginAnswerReveal = () => {
        // Flag-on, a tap during the pending window (panel slide, quest fetch) must not
        // cancel the single route-owned reveal; cancellation arms at claim time instead.
        if (userHasFeature('instant_reply_nav')) return
        this.answerRevealCancellationCleanup?.()
        this.answerRevealCancellationCleanup = this.attachAnswerRevealCancellation()
    }

    private beginClaimedAnswerReveal = () => {
        if (!userHasFeature('instant_reply_nav')) return
        this.answerRevealCancellationCleanup?.()
        this.answerRevealCancellationCleanup = this.attachAnswerRevealCancellation()
    }

    private completeAnswerReveal = (intent: AnswerRevealIntent, result: AnswerRevealResult) => {
        this.answerRevealCancellationCleanup?.()
        this.answerRevealCancellationCleanup = undefined
        if (result !== 'done') return
        if (intent.shakeQuestId) this.shakeQuest(intent.shakeQuestId)
        if (intent.shakeQuestByAnswer) this.shakeQuestByAnswer(intent.answerId)
        if (intent.shake) this.shake()
        if (intent.highlight) this.highlightAnswer(intent.answerId)
    }

    private buildAnswerRevealIntent = (
        answerId: string,
        options: RevealAnswerOptions,
    ): AnswerRevealIntent => {
        const questId = options.questId || this.filter?.questId || ''
        return {
            key: `${questId}:${answerId}`,
            questId: questId || undefined,
            answerId,
            contextScroll:
                options.contextScroll ||
                (this.pendingOpenPlacement?.contextScroll &&
                    this.pendingOpenPlacement.questId === String(questId) &&
                    this.pendingOpenPlacement.answerId === answerId),
            deadlineAt: options.deadlineAt || Date.now() + 9000,
            // After a pre-paint open placement, the reveal pass is verification only —
            // any residual correction must be instant, never the staged rewind.
            instant:
                options.instant ??
                (this.hasAppliedOpenPlacement(questId, answerId) ||
                    userHasFeature('instant_reply_nav')),
            highlight: options.highlight !== false,
            shake: options.shake === true,
            shakeQuestByAnswer: options.shakeQuestByAnswer === true,
            shakeQuestId: options.shakeQuestId,
            onRevealed: options.onRevealed,
        }
    }

    requestAnswerReveal = (
        answerId: string,
        options: RevealAnswerOptions = {},
    ): Promise<AnswerScrollResult> => {
        if (!answerId) return Promise.resolve('not-in-quest')
        return this.answerRevealCoordinator.request(
            this.buildAnswerRevealIntent(String(answerId), options),
        )
    }

    ensureAnswerReveal = (
        answerId: string,
        options: RevealAnswerOptions = {},
    ): Promise<AnswerScrollResult> => {
        if (!answerId) return Promise.resolve('not-in-quest')
        return this.answerRevealCoordinator.ensure(
            this.buildAnswerRevealIntent(String(answerId), options),
        )
    }

    clearEnsuredAnswerReveal = () => {
        this.cancelPendingAnswerScroll()
        this.answerRevealCoordinator.resetEnsureKey()
    }

    cancelAnswerReveal = (questId: string, answerId: string) => {
        this.answerRevealCoordinator.cancelKey(`${questId || ''}:${answerId}`)
    }

    scrollToAnswerDeepLink = (
        answerId: string,
        options: AnswerScrollOptions = {},
    ): Promise<AnswerScrollResult> => {
        return this.requestAnswerReveal(answerId, {
            deadlineAt: options.deadlineAt,
            instant: options.instant,
            highlight: false,
        })
    }

    scrollToBottom = (callback?: () => void) => {
        const scrollContainer = this.getScrollContainer()
        if (!scrollContainer) return

        // Defer to next frame so layout (including keyboard placeholder) has settled
        requestAnimationFrame(() => {
            const BIG_JUMP_THRESHOLD = 800 // px; above this, jump instead of animating from top
            const getTargetScroll = () => {
                const bottomGutter = scrollContainer.querySelector<HTMLElement>(
                    '[data-thread-bottom-gutter="true"]',
                )
                const bottomGutterHeight = bottomGutter?.offsetHeight || 0

                return Math.max(
                    0,
                    scrollContainer.scrollHeight -
                        scrollContainer.clientHeight -
                        bottomGutterHeight,
                )
            }

            const currentTop = scrollContainer.scrollTop || 0
            const targetTop = getTargetScroll()
            const distance = Math.abs(targetTop - currentTop)

            const performSmoothRetry = () => {
                const checkIfReachedBottom = () => {
                    const cur = scrollContainer.scrollTop
                    const contentBottom = getTargetScroll()
                    // Be strict so we truly land at bottom; a large tolerance can
                    // incorrectly treat near-bottom as done and leave input obscured
                    return Math.abs(cur - contentBottom) <= 5
                }
                this.scrollWithRetry(
                    scrollContainer,
                    getTargetScroll,
                    checkIfReachedBottom,
                    callback,
                )
            }

            if (distance > BIG_JUMP_THRESHOLD) {
                // Avoid long animated scrolls across the whole thread. Panels the user
                // cannot see jump directly; a watched panel lands one glide-length short
                // so the retry below animates the visible remainder instead of
                // hard-cutting the viewport.
                if (this.canAnimateScroll(scrollContainer)) {
                    const shortOfTarget = targetTop - this.glideDistance(scrollContainer)
                    if (shortOfTarget > currentTop) {
                        scrollContainer.scrollTop = shortOfTarget
                    }
                } else {
                    scrollContainer.scrollTop = targetTop
                }
                // Fine-tune on next frame in case content height changes
                requestAnimationFrame(() => performSmoothRetry())
            } else {
                // Short distance — use smooth scrolling with retry
                performSmoothRetry()
            }
        })
    }

    highlightAnswer = (answerId: string, retryCount = 0) => {
        if (!answerId) return

        const answerElement = this.panelRef?.querySelector(`.answer-highlight-${answerId}`)
        if (!answerElement) {
            // Retry a few times with requestAnimationFrame for immediate response
            if (retryCount < 3) {
                requestAnimationFrame(() => {
                    this.highlightAnswer(answerId, retryCount + 1)
                })
            }
            return
        }

        // Title breadcrumbs live outside the answer surface but are visually part of the same
        // stream card, so animate both surfaces together.
        const highlightTargets = getAnswerHighlightTargets(answerElement)
        // One arrival is one flash. Opening a thread runs two highlight paths —
        // the reveal's own completion and openRight's backstop — and restarting
        // the animation mid-flight reads as the surface blinking twice.
        if (highlightTargets.some(element => element.classList.contains('highlight'))) return
        highlightTargets.forEach(element => element.classList.add('highlight'))

        // The CSS owns the duration (styles/_common.scss); read it back so the class outlives
        // every keyframe, and holds the reduced-motion static wash for the same beat.
        setTimeout(() => {
            highlightTargets.forEach(element => element.classList.remove('highlight'))
        }, getAnswerHighlightHoldMs(answerElement))
    }
}

export const emptyPanel = () => new PanelState({ empty: true })
